<script setup lang="ts">
import { computed } from 'vue';
import { thumbUrl } from '@/utils/assetUrl';
import type { ResearchNodeView, ResearchStatus } from '@/types/research';
import AppInfoCard from '@/components/shared/AppInfoCard.vue';
import ResearchTooltipContent from '@/components/research/ResearchTooltipContent.vue';

/**
 * Noeud compact de l'arbre de recherche : image, nom, niveau, etat. Grise si
 * verrouille (prerequis non remplis). Clic -> selection (le detail + bouton
 * Lancer s'affichent dans la colonne de droite).
 */
const props = defineProps<{
  node: ResearchNodeView;
  selected?: boolean;
  /** % de progression si la recherche est en cours (pour le reveal d'image). */
  progress?: number;
  /** Niveau du Laboratoire central de la planète (palier max atteignable). */
  laboCentralLevel?: number | null;
  /** Niveau du Centre de recherche de la planète (labo de base). */
  centreLevel?: number | null;
  /** Affichage CARRÉ compact (zoom « niveau » de l'arbre) : vignette en haut,
   *  nom dessous, pour s'aligner dans la grille des niveaux. */
  square?: boolean;
}>();
defineEmits<{ select: [node: ResearchNodeView] }>();

// Bâtiment verrouillé : le LABO producteur de la recherche (Centre de recherche ou
// Laboratoire central selon ProductionCategory) n'atteint pas le NIVEAU requis
// -> grisé + badge « N{niveau} ». (niveau null/undefined = vue globale -> neutre.)
const labLevel = computed(() => props.node.productionCategory === 'LaboratoireCentral'
  ? props.laboCentralLevel : props.centreLevel);
const labLabel = computed(() => props.node.productionCategory === 'LaboratoireCentral'
  ? 'Laboratoire central' : 'Centre de recherche');
const tierLocked = computed(() =>
  (props.node.requiredBuildingLevel ?? 0) > 0
  && labLevel.value != null
  && labLevel.value < (props.node.requiredBuildingLevel ?? 0));

// Dossier de représentation (wiki §24.7-ter.8) : "buildings" si la recherche
// débloque un bâtiment (image réutilisée), sinon "research_symbols".
const imgFolder = computed(() => props.node.imageFolder || 'research_symbols');
const img = computed(() =>
  props.node.imageCode ? thumbUrl(imgFolder.value, props.node.imageCode, 96) : null,
);

// Fond decoratif (basse qualite) derriere le bloc -- ambiance par recherche.
// Pour un symbole de recherche on a un dossier de fonds dédié ; pour une image
// de bâtiment réutilisée, le fond reprend la même image (atténuée par le CSS).
const bgImg = computed(() => {
  if (!props.node.imageCode) return null;
  const folder = imgFolder.value === 'research_symbols' ? 'research_backgrounds' : imgFolder.value;
  return thumbUrl(folder, props.node.imageCode, 192);
});

// Recherche en cours : on devoile l'image progressivement de gauche a droite
// (comme la construction des batiments). Couche couleur clippee a `progress`%.
const isInProgress = computed(() => props.node.status === 'InProgress');
const revealClip = computed(() =>
  `inset(0 ${(100 - Math.min(100, Math.max(0, props.progress ?? 0))).toFixed(1)}% 0 0)`,
);

const statusTone = computed<'success' | 'warning' | 'info' | 'danger'>(() => {
  const m: Record<ResearchStatus, 'success' | 'warning' | 'info' | 'danger'> = {
    Available: 'info', InProgress: 'warning', Maxed: 'success', Locked: 'danger',
  };
  return m[props.node.status];
});

const statusLabel = computed<string>(() => {
  const m: Record<ResearchStatus, string> = {
    Available: 'Disponible', InProgress: 'En cours', Maxed: 'Max', Locked: 'Verrouillé',
  };
  return m[props.node.status];
});
</script>

<template>
  <AppInfoCard placement="right" :width="340" :show-after="300">
    <button
    type="button"
    class="rt-node"
    :class="{
      'is-selected': selected,
      'is-locked': node.status === 'Locked',
      'is-inprogress': node.status === 'InProgress',
      'is-maxed': node.status === 'Maxed',
      'is-advanced-locked': tierLocked,
      'is-square': square,
    }"
    :data-research-id="node.id"
    @click="$emit('select', node)"
  >
    <span
      v-if="bgImg"
      class="rt-node-bg"
      :style="{ backgroundImage: `url(${bgImg})` }"
      aria-hidden="true"
    />
    <div class="rt-node-thumb" :class="{ 'is-revealing': isInProgress }">
      <template v-if="img">
        <!-- En cours : base grisee + couche couleur revelee de gauche a droite. -->
        <img :src="img" :alt="node.name" loading="lazy" class="rt-node-img" :class="{ 'rt-node-img-base': isInProgress }" />
        <img
          v-if="isInProgress"
          :src="img" :alt="node.name" loading="lazy"
          class="rt-node-img rt-node-img-reveal"
          :style="{ clipPath: revealClip }"
        />
      </template>
      <el-icon v-else class="rt-node-thumb-fallback"><Cpu /></el-icon>
      <span v-if="node.status === 'Locked'" class="rt-node-lock"><el-icon><Lock /></el-icon></span>
      <!-- Palier : badge Labo central requis (rouge si niveau insuffisant). -->
      <span
        v-if="(node.tier ?? 0) > 0"
        class="rt-node-adv"
        :class="{ 'is-missing': tierLocked }"
        :title="tierLocked
          ? `Nécessite un ${labLabel} niveau ${node.requiredBuildingLevel} (actuel : ${labLevel ?? 0})`
          : `${labLabel} niveau ${node.requiredBuildingLevel}`"
      >P{{ node.tier }}</span>
    </div>
    <div class="rt-node-body">
      <strong class="rt-node-name">{{ node.name }}</strong>
      <div class="rt-node-meta">
        <span class="rt-node-level text-mono">N{{ node.currentLevel }}/{{ node.maxLevel }}</span>
        <el-tag :type="statusTone" size="small" effect="dark" class="rt-node-tag">{{ statusLabel }}</el-tag>
      </div>
    </div>
    </button>
    <template #content>
      <ResearchTooltipContent :node="node" :labo-central-level="laboCentralLevel" :centre-level="centreLevel" />
    </template>
  </AppInfoCard>
</template>

<style lang="scss" scoped>
.rt-node {
  position: relative;
  display: flex;
  align-items: center;
  gap: $space-2;
  width: 190px;
  padding: $space-2;
  text-align: left;
  border: 1px solid $border-default;
  border-radius: $radius-md;
  background: rgba(14, 20, 34, 0.92);
  color: $text-primary;
  cursor: pointer;
  overflow: hidden;
  transition: all $duration-fast $easing-default;

  &:hover { border-color: rgba($accent-primary, 0.6); transform: translateY(-1px); }
  &.is-selected { border-color: $accent-primary; @include neon-glow($accent-primary); }
  &.is-locked { opacity: 0.5; }
  &.is-locked:hover { opacity: 0.75; }
  &.is-inprogress { border-color: rgba($color-warning, 0.5); }
  &.is-maxed { border-color: rgba($color-success, 0.45); }
  // Palier avancé verrouillé (pas de Labo central) : grisé + bord ambré.
  &.is-advanced-locked { opacity: 0.55; border-color: rgba($color-warning, 0.5); }
  &.is-advanced-locked:hover { opacity: 0.8; }

  // --- Variante CARRÉE (zoom « niveau ») : vignette en haut, nom dessous. ----
  &.is-square {
    flex-direction: column;
    align-items: stretch;
    width: 96px;
    gap: 4px;
    padding: $space-1;

    .rt-node-thumb { width: 100%; height: 72px; }
    .rt-node-name { @include truncate(2); font-size: $font-size-xs; text-align: center; }
    // En carré on masque la ligne meta (tag) — l'état passe par le bord/couleur ;
    // le niveau N/max reste lisible au survol (tooltip) et dans le détail.
    .rt-node-meta { justify-content: center; }
    .rt-node-tag { display: none; }
    .rt-node-bg { mask-image: linear-gradient(180deg, transparent 0%, #000 60%);
      -webkit-mask-image: linear-gradient(180deg, transparent 0%, #000 60%); }
  }
}

// Badge « palier avancé » (Labo central) en coin de la vignette.
.rt-node-adv {
  position: absolute;
  top: 2px;
  left: 2px;
  font-size: 11px;
  line-height: 1;
  padding: 2px 3px;
  border-radius: $radius-sm;
  background: rgba($accent-primary, 0.25);
  border: 1px solid rgba($accent-primary, 0.5);

  &.is-missing {
    background: rgba($color-warning, 0.22);
    border-color: rgba($color-warning, 0.6);
  }
}

// Fond decoratif : couvre tout le bloc, attenue + voile sombre pour lisibilite.
.rt-node-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  background-size: cover;
  background-position: center;
  opacity: 0.16;
  pointer-events: none;
  mask-image: linear-gradient(90deg, transparent 0%, #000 45%);
  -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 45%);
}
.rt-node-thumb {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: $radius-sm;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;

  .rt-node-thumb-fallback { font-size: 22px; color: $color-neon-cyan; }
}
.rt-node-img { width: 100%; height: 100%; object-fit: cover; }
// Reveal construction : base desaturee + couche couleur clippee a gauche.
.rt-node-img-base { filter: grayscale(1) brightness(0.5); }
.rt-node-img-reveal {
  position: absolute;
  inset: 0;
  transition: clip-path 1s linear;
}
.rt-node-lock {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(8, 12, 20, 0.55);
  color: $color-danger;
}

.rt-node-body { position: relative; z-index: 1; min-width: 0; flex: 1; }
.rt-node-name {
  display: block;
  font-size: $font-size-sm;
  line-height: 1.15;
  @include truncate(2);
}
.rt-node-meta {
  display: flex;
  align-items: center;
  gap: $space-1;
  margin-top: 2px;
}
.rt-node-level { font-size: $font-size-xs; color: $text-secondary; }
.rt-node-tag { transform: scale(0.85); transform-origin: left; }
</style>
